import React, { useEffect, useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Button from "../../common/Button";
import apiService from "../../../services/ApiService";
import { apiRoutes } from "../../../shared/apiRoutes";
import { Party } from "../../../shared/interfaces";
import { dateInputToIsoUtc, getTodayDateIST, toISTDateString } from "../../../utils/util";
import {
  HAVALA_PL_DISPLAY,
  HAVALA_PL_LABEL,
  HAVALA_PL_PARTY_ID,
  HAVALA_PL_PARTY_NAME,
  normalizePlAccountInput,
} from "../../../utils/havalaPl";

interface HavalaFormValues {
  recordDate: string;
  lena: string;
  dena: string;
  amount: string;
  city: string;
  remark: string;
  token: string;
  freezeDate: boolean;
  freezeReceiver: boolean;
  freezeSender: boolean;
}

interface CreateHavalaSideDrawerDialogProps {
  onClose: () => void;
  isOpen?: boolean;
  editId?: number | null;
  onSuccess?: () => void; // Callback to refresh list after successful save
}

const validationSchema = Yup.object().shape({
  recordDate: Yup.string()
    .required("Record Date is required"),
  // Note: dena and lena are validated separately via party validation state
  // They are not part of Formik values, so we handle validation manually
  amount: Yup.string()
    .required("Amount is required")
    .matches(/^-?\d+$/, "Amount must be a valid number (can be negative)")
    .max(11, "Amount must be at most 11 characters (including negative sign)"),
  city: Yup.string()
    .max(100, "City must be at most 100 characters"),
  remark: Yup.string()
    .max(500, "Remark must be at most 500 characters"),
  token: Yup.string()
    .max(100, "Token must be at most 100 characters"),
});

const getTodayDate = getTodayDateIST;

const CreateHavalaSideDrawerDialog: React.FC<CreateHavalaSideDrawerDialogProps> = ({ onClose, isOpen = true, editId = null, onSuccess }) => {
  const [parties, setParties] = useState<Party[]>([]);
  const [initialFormValues, setInitialFormValues] = useState<HavalaFormValues | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  // Store values to set on inputs once refs are ready
  const [pendingLenaValue, setPendingLenaValue] = useState<string | null>(null);
  const [pendingDenaValue, setPendingDenaValue] = useState<string | null>(null);
  
  // Party validation state for Dena and Lena
  const [denaCode, setDenaCode] = useState<string>('');
  const [validatedDenaCode, setValidatedDenaCode] = useState<string>('');
  const [denaValidation, setDenaValidation] = useState<{ isValid: boolean; error: string; partyName: string; partyId: number | null; currentBalance?: number }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null,
    currentBalance: undefined
  });
  
  const [lenaCode, setLenaCode] = useState<string>('');
  const [validatedLenaCode, setValidatedLenaCode] = useState<string>('');
  const [lenaValidation, setLenaValidation] = useState<{ isValid: boolean; error: string; partyName: string; partyId: number | null; currentBalance?: number }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null,
    currentBalance: undefined
  });
  
  const recordDateInputRef = useRef<HTMLInputElement>(null);
  const lenaInputRef = useRef<HTMLInputElement>(null);
  const denaInputRef = useRef<HTMLInputElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const remarkInputRef = useRef<HTMLInputElement>(null);
  const saveAndContinueButtonRef = useRef<HTMLButtonElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const isSubmittingRef = useRef(false);

  // Function to move focus to next field
  const moveToNextField = (currentRef: React.RefObject<HTMLInputElement | null>, nextRef: React.RefObject<HTMLInputElement | null>) => {
    if (nextRef.current) {
      setTimeout(() => {
        nextRef.current?.focus();
      }, 0);
    }
  };

  // Function to move focus to next field by name
  const moveToNextFieldByName = (currentFieldName: string, nextFieldName: string) => {
    setTimeout(() => {
      const nextInput = document.querySelector(`input[name="${nextFieldName}"]`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }, 0);
  };

  // Validate party code format
  const validatePartyCodeFormat = (code: string): { isValid: boolean; error: string } => {
    if (!code || code.trim().length === 0) {
      return { isValid: false, error: 'Party code is required' };
    }
    if (code.length < 1) {
      return { isValid: false, error: 'Party code must be at least 1 character' };
    }
    if (code.length > 5) {
      return { isValid: false, error: 'Party code must be at most 5 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(code)) {
      return { isValid: false, error: 'Party code must be alphanumeric' };
    }
    return { isValid: true, error: '' };
  };

  // Validate and fetch party by code (for Dena)
  const validateAndFetchDenaParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();

    const plLabel =
      normalizePlAccountInput(code.trim()) || normalizePlAccountInput(trimmedCode);
    if (plLabel) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: HAVALA_PL_PARTY_NAME,
        partyId: HAVALA_PL_PARTY_ID,
        currentBalance: undefined as number | undefined
      };
      setDenaValidation(validationResult);
      setValidatedDenaCode(plLabel);
      if (denaInputRef.current) {
        denaInputRef.current.value = HAVALA_PL_DISPLAY;
      }
      setDenaCode(plLabel);
      return validationResult;
    }

    const formatValidation = validatePartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setDenaValidation(validationResult);
      setValidatedDenaCode('');
      return validationResult;
    }

    const foundParty = (parties || []).find(p => p.partyCode.toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.partyName,
        partyId: foundParty.id,
        currentBalance: foundParty.currentBalance
      };
      setDenaValidation(validationResult);
      setValidatedDenaCode(trimmedCode);
      if (denaInputRef.current) {
        denaInputRef.current.value = `${foundParty.partyCode} - ${foundParty.partyName}`;
      }
      setDenaCode(trimmedCode);
      return validationResult;
    }

    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id,
          currentBalance: partyData.currentBalance
        };
        setDenaValidation(validationResult);
        setValidatedDenaCode(trimmedCode);
        if (denaInputRef.current) {
          denaInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setDenaCode(trimmedCode);
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setDenaValidation(validationResult);
        setValidatedDenaCode('');
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setDenaValidation(validationResult);
      setValidatedDenaCode('');
      return validationResult;
    }
  };

  // Validate and fetch party by code (for Lena)
  const validateAndFetchLenaParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();

    const plLabel =
      normalizePlAccountInput(code.trim()) || normalizePlAccountInput(trimmedCode);
    if (plLabel) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: HAVALA_PL_PARTY_NAME,
        partyId: HAVALA_PL_PARTY_ID,
        currentBalance: undefined as number | undefined
      };
      setLenaValidation(validationResult);
      setValidatedLenaCode(plLabel);
      if (lenaInputRef.current) {
        lenaInputRef.current.value = HAVALA_PL_DISPLAY;
      }
      setLenaCode(plLabel);
      return validationResult;
    }

    const formatValidation = validatePartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setLenaValidation(validationResult);
      setValidatedLenaCode('');
      return validationResult;
    }

    const foundParty = (parties || []).find(p => p.partyCode.toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.partyName,
        partyId: foundParty.id,
        currentBalance: foundParty.currentBalance
      };
      setLenaValidation(validationResult);
      setValidatedLenaCode(trimmedCode);
      if (lenaInputRef.current) {
        lenaInputRef.current.value = `${foundParty.partyCode} - ${foundParty.partyName}`;
      }
      setLenaCode(trimmedCode);
      return validationResult;
    }

    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id,
          currentBalance: partyData.currentBalance
        };
        setLenaValidation(validationResult);
        setValidatedLenaCode(trimmedCode);
        if (lenaInputRef.current) {
          lenaInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setLenaCode(trimmedCode);
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setLenaValidation(validationResult);
        setValidatedLenaCode('');
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setLenaValidation(validationResult);
      setValidatedLenaCode('');
      return validationResult;
    }
  };

  // Handle Dena party code change
  const handleDenaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    const isNewValue = codeOnly !== validatedDenaCode;
    
    if (isNewValue) {
      setDenaCode(codeOnly);
      setValidatedDenaCode('');
      setDenaValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (denaInputRef.current && value !== codeOnly) {
        denaInputRef.current.value = codeOnly;
      }
    } else {
      setDenaCode(codeOnly);
    }
  };

  // Handle Dena party code blur
  const handleDenaCodeBlur = async () => {
    const value = denaInputRef.current?.value || denaCode;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate) {
      if (codeToValidate !== validatedDenaCode) {
        await validateAndFetchDenaParty(codeToValidate);
      }
    } else {
      setDenaValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedDenaCode('');
      if (denaInputRef.current) {
        denaInputRef.current.value = '';
      }
    }
  };

  // Handle Dena party code Enter key
  const handleDenaCodeEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = denaInputRef.current?.value || denaCode;
      const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
      const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
      
      if (codeToValidate) {
        let validationResult;
        if (codeToValidate !== validatedDenaCode) {
          validationResult = await validateAndFetchDenaParty(codeToValidate);
        } else {
          validationResult = {
            isValid: denaValidation.isValid,
            error: denaValidation.error,
            partyName: denaValidation.partyName,
            partyId: denaValidation.partyId
          };
        }
        
        if (validationResult.isValid && validationResult.partyId !== null) {
          setTimeout(() => {
            moveToNextFieldByName('dena', 'amount');
          }, 50);
        } else {
          denaInputRef.current?.focus();
        }
      } else {
        denaInputRef.current?.focus();
      }
    }
  };

  // Handle Lena party code change
  const handleLenaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    const isNewValue = codeOnly !== validatedLenaCode;
    
    if (isNewValue) {
      setLenaCode(codeOnly);
      setValidatedLenaCode('');
      setLenaValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (lenaInputRef.current && value !== codeOnly) {
        lenaInputRef.current.value = codeOnly;
      }
    } else {
      setLenaCode(codeOnly);
    }
  };

  // Handle Lena party code blur
  const handleLenaCodeBlur = async () => {
    const value = lenaInputRef.current?.value || lenaCode;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate) {
      if (codeToValidate !== validatedLenaCode) {
        await validateAndFetchLenaParty(codeToValidate);
      }
    } else {
      setLenaValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedLenaCode('');
      if (lenaInputRef.current) {
        lenaInputRef.current.value = '';
      }
    }
  };

  // Handle Lena party code Enter key
  const handleLenaCodeEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = lenaInputRef.current?.value || lenaCode;
      const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
      const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
      
      if (codeToValidate) {
        let validationResult;
        if (codeToValidate !== validatedLenaCode) {
          validationResult = await validateAndFetchLenaParty(codeToValidate);
        } else {
          validationResult = {
            isValid: lenaValidation.isValid,
            error: lenaValidation.error,
            partyName: lenaValidation.partyName,
            partyId: lenaValidation.partyId
          };
        }
        
        if (validationResult.isValid && validationResult.partyId !== null) {
          setTimeout(() => {
            moveToNextFieldByName('lena', 'dena');
          }, 50);
        } else {
          lenaInputRef.current?.focus();
        }
      } else {
        lenaInputRef.current?.focus();
      }
    }
  };

  // Fetch Havala data when editing
  useEffect(() => {
    if (isOpen && editId) {
      setLoadingData(true);
      apiService.get(apiRoutes.transactions.getHavalaById(editId))
        .then((response: any) => {
          const data = response.data || response;
          
          // Format date from createdAt or modifiedAt
          const dateValue = data.recordDate || data.createdAt || data.modifiedAt;
          const recordDate = dateValue ? toISTDateString(new Date(dateValue)) : getTodayDate();
          
          // Set initial form values
          setInitialFormValues({
            recordDate: recordDate,
            lena: data.receiverPartyCode || '',
            dena: data.senderPartyCode || '',
            amount: data.amount?.toString() || '',
            city: data.city || '',
            remark: data.remarks || '',
            token: data.token || '',
            freezeDate: false,
            freezeReceiver: false,
            freezeSender: false,
          });
          
          // Set party codes and validation state
          if (data.receiverPartyId === HAVALA_PL_PARTY_ID) {
            setLenaCode(HAVALA_PL_LABEL);
            setValidatedLenaCode(HAVALA_PL_LABEL);
            setLenaValidation({
              isValid: true,
              error: '',
              partyName: HAVALA_PL_PARTY_NAME,
              partyId: HAVALA_PL_PARTY_ID
            });
            setPendingLenaValue(HAVALA_PL_DISPLAY);
          } else if (data.receiverPartyCode || data.receiverPartyName) {
            const lenaCode = data.receiverPartyCode || '';
            const lenaName = data.receiverPartyName || '';
            setLenaCode(lenaCode);
            setValidatedLenaCode(lenaCode.toUpperCase());
            setLenaValidation({
              isValid: true,
              error: '',
              partyName: lenaName,
              partyId:
                typeof data.receiverPartyId === 'number' ? data.receiverPartyId : null
            });
            // Store value to set once ref is ready
            setPendingLenaValue(lenaName ? `${lenaCode} - ${lenaName}` : lenaCode);
          }

          if (data.senderPartyId === HAVALA_PL_PARTY_ID) {
            setDenaCode(HAVALA_PL_LABEL);
            setValidatedDenaCode(HAVALA_PL_LABEL);
            setDenaValidation({
              isValid: true,
              error: '',
              partyName: HAVALA_PL_PARTY_NAME,
              partyId: HAVALA_PL_PARTY_ID
            });
            setPendingDenaValue(HAVALA_PL_DISPLAY);
          } else if (data.senderPartyCode || data.senderPartyName) {
            const denaCode = data.senderPartyCode || '';
            const denaName = data.senderPartyName || '';
            setDenaCode(denaCode);
            setValidatedDenaCode(denaCode.toUpperCase());
            setDenaValidation({
              isValid: true,
              error: '',
              partyName: denaName,
              partyId: typeof data.senderPartyId === 'number' ? data.senderPartyId : null
            });
            // Store value to set once ref is ready
            setPendingDenaValue(denaName ? `${denaCode} - ${denaName}` : denaCode);
          }
          
          setLoadingData(false);
        })
        .catch((error: any) => {
          console.error('Error fetching Havala data:', error);
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: 'Failed to load Havala data', type: 'error' }
          }));
          setLoadingData(false);
        });
    } else if (isOpen && !editId) {
      // Reset form when creating new
      setInitialFormValues(null);
      setLenaCode('');
      setValidatedLenaCode('');
      setLenaValidation({ isValid: false, error: '', partyName: '', partyId: null });
      setDenaCode('');
      setValidatedDenaCode('');
      setDenaValidation({ isValid: false, error: '', partyName: '', partyId: null });
      setPendingLenaValue(null);
      setPendingDenaValue(null);
      if (lenaInputRef.current) lenaInputRef.current.value = '';
      if (denaInputRef.current) denaInputRef.current.value = '';
    }
  }, [isOpen, editId]);

  // Set input values once refs are ready (for edit mode)
  useEffect(() => {
    if (!isOpen || !editId || loadingData) {
      return;
    }

    // Only proceed if we have pending values to set
    if (!pendingLenaValue && !pendingDenaValue) {
      return;
    }

    // Store values in local variables to avoid stale closure issues
    const lenaValueToSet = pendingLenaValue;
    const denaValueToSet = pendingDenaValue;

    // Use requestAnimationFrame to ensure DOM is ready, then try multiple times if needed
    let attempts = 0;
    const maxAttempts = 20; // Increased attempts for production environments
    
    const trySetValues = () => {
      attempts++;
      
      let lenaSet = false;
      let denaSet = false;
      
      if (lenaValueToSet && lenaInputRef.current) {
        lenaInputRef.current.value = lenaValueToSet;
        setPendingLenaValue(null);
        lenaSet = true;
      }
      
      if (denaValueToSet && denaInputRef.current) {
        denaInputRef.current.value = denaValueToSet;
        setPendingDenaValue(null);
        denaSet = true;
      }
      
      // If both values are set or max attempts reached, stop
      if ((lenaSet || !lenaValueToSet) && (denaSet || !denaValueToSet) || attempts >= maxAttempts) {
        return;
      }
      
      // Try again after a short delay
      setTimeout(trySetValues, 50);
    };
    
    // Start trying after a small delay to ensure Formik has rendered
    // Use requestAnimationFrame for better timing in production
    requestAnimationFrame(() => {
      setTimeout(trySetValues, 150);
    });
  }, [isOpen, editId, loadingData, pendingLenaValue, pendingDenaValue]);

  // Function to fetch parties and update balance information.
  // Returns refreshed balances for the currently selected Lena/Dena (needed after Save & Continue
  // because restoring frozen party state must not wipe the new balances).
  const fetchPartiesAndUpdateBalance = async (): Promise<{
    lenaBalance?: number;
    denaBalance?: number;
  }> => {
    try {
      const response = await apiService.post(apiRoutes.parties.lite, {
        page: 1,
        pageSize: 10000,
        orderBy: 'partyCode',
        orderDirection: 'asc'
      });
      const partiesData = Array.isArray(response)
        ? response
        : (response?.data || response?.Data || []);
      let lenaBalance: number | undefined;
      let denaBalance: number | undefined;
      if (Array.isArray(partiesData) && partiesData.length > 0) {
        setParties(partiesData);
        
        // Update balance information for Lena and Dena even if frozen
        if (lenaValidation.partyId) {
          const lenaParty = partiesData.find((p: Party) => p.id === lenaValidation.partyId);
          if (lenaParty) {
            lenaBalance = lenaParty.currentBalance ?? lenaParty.openingBalance ?? 0;
            setLenaValidation((prev) => ({
              ...prev,
              currentBalance: lenaBalance
            }));
          }
        }
        
        if (denaValidation.partyId) {
          const denaParty = partiesData.find((p: Party) => p.id === denaValidation.partyId);
          if (denaParty) {
            denaBalance = denaParty.currentBalance ?? denaParty.openingBalance ?? 0;
            setDenaValidation((prev) => ({
              ...prev,
              currentBalance: denaBalance
            }));
          }
        }
      }
      return { lenaBalance, denaBalance };
    } catch (error: any) {
      console.error('Error fetching parties:', error);
      return {};
    }
  };

  // Fetch parties when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchPartiesAndUpdateBalance();
    } else {
      // Reset when drawer closes
      isSubmittingRef.current = false;
      setLenaCode('');
      setValidatedLenaCode('');
      setLenaValidation({ isValid: false, error: '', partyName: '', partyId: null });
      setDenaCode('');
      setValidatedDenaCode('');
      setDenaValidation({ isValid: false, error: '', partyName: '', partyId: null });
    }
  }, [isOpen]);

  // Handle ESC key to close dialog
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !e.defaultPrevented) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  // Focus on Lena (Receiver) field when form opens (both add and edit mode)
  useEffect(() => {
    if (isOpen) {
      // Wait for form to be ready - in edit mode, wait for data to load
      if (editId && loadingData) {
        return; // Don't focus yet if still loading edit data
      }
      // Small delay to ensure the form is fully rendered
      setTimeout(() => {
        lenaInputRef.current?.focus();
      }, 150);
    }
  }, [isOpen, editId, loadingData]);

  const handleSubmit = async (values: HavalaFormValues, formikHelpers?: any, overridePartyIds?: { denaPartyId?: number | null, lenaPartyId?: number | null }) => {
    if (formikHelpers?.setSubmitting) {
      formikHelpers.setSubmitting(true);
    }
    try {
      // Use override party IDs if provided, otherwise use state
      const denaPartyId = overridePartyIds?.denaPartyId ?? denaValidation.partyId;
      const lenaPartyId = overridePartyIds?.lenaPartyId ?? lenaValidation.partyId;
      
      // Validate that both LENA and DENA have valid party IDs (0 = P&L)
      if (lenaPartyId === null || denaPartyId === null) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'Please enter valid party codes for both LENA and DENA', type: 'error' }
        }));
        if (formikHelpers?.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        return false;
      }

      // Validate that LENA and DENA are not the same party
      if (lenaPartyId === denaPartyId) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'LENA and DENA cannot be the same party', type: 'error' }
        }));
        if (formikHelpers?.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        return false;
      }

      // Convert amount to number
      const amountValue = parseFloat(values.amount);
      if (isNaN(amountValue) || amountValue === 0) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'Please enter a valid amount (cannot be zero)', type: 'error' }
        }));
        if (formikHelpers?.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        return false;
      }

      // Prepare request body according to API specification (recordDate = business date; ISO UTC)
      const requestBody: {
        senderPartyId: number;
        receiverPartyId: number;
        amount: number;
        city?: string;
        token?: string;
        remarks?: string;
        recordDate?: string;
      } = {
        senderPartyId: denaPartyId!, // DENA is the sender
        receiverPartyId: lenaPartyId!, // LENA is the receiver
        amount: amountValue,
        city: values.city || undefined,
        token: values.token || undefined,
        remarks: values.remark || undefined,
      };

      if (values.recordDate?.trim()) {
        requestBody.recordDate = dateInputToIsoUtc(values.recordDate);
      }

      // Remove undefined fields
      Object.keys(requestBody).forEach((key) => {
        if (requestBody[key as keyof typeof requestBody] === undefined) {
          delete requestBody[key as keyof typeof requestBody];
        }
      });

      // Call the appropriate API endpoint
      if (editId) {
        // Update existing Havala
        await apiService.put(apiRoutes.transactions.updateHavala(editId), requestBody);
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'Havala transaction updated successfully', type: 'success' }
        }));
      } else {
        // Create new Havala
        await apiService.post(apiRoutes.transactions.createHavala, requestBody);
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'Havala transaction created successfully', type: 'success' }
        }));
      }

      // Call onSuccess callback to refresh list if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Dispatch custom event to refresh Havala list (for global dialog or when onSuccess is not provided)
      window.dispatchEvent(new CustomEvent('havala-saved', {
        detail: { editId, isEdit: !!editId }
      }));
      
      // Set submitting to false if formikHelpers provided
      if (formikHelpers?.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      
      // Return success status
      return true;
    } catch (error: any) {
      console.error(editId ? 'Error updating Havala transaction:' : 'Error creating Havala transaction:', error);
      
      // Show error message
      const errorMessage = error?.message || error?.error || 
        (editId ? 'Failed to update Havala transaction' : 'Failed to create Havala transaction');
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: errorMessage, type: 'error' }
      }));

      // Set submitting to false on error
      if (formikHelpers?.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }

      // Return failure status
      return false;
    }
  };


  // Default initial values
  const defaultInitialValues: HavalaFormValues = {
    recordDate: getTodayDate(),
    lena: "",
    dena: "",
    amount: "",
    city: "",
    remark: "",
    token: "",
    freezeDate: false,
    freezeReceiver: false,
    freezeSender: false,
  };

  // Use initialFormValues if available (for edit mode), otherwise use defaults
  const formInitialValues = initialFormValues || defaultInitialValues;

  // Don't render form until data is loaded (for edit mode)
  if (editId && loadingData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <Formik
      key={editId || 'new'} // Force reinitialize when editId changes
      initialValues={formInitialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, setFieldValue, validateField, setFieldError, submitForm, resetForm, errors, validateForm, setSubmitting }) => {
        // Helper function to handle checkbox changes
        const handleFreezeCheckboxChange = (fieldName: 'freezeDate' | 'freezeReceiver' | 'freezeSender', checked: boolean) => {
          setFieldValue(fieldName, checked, false);
        };
        
        return (
        <Form className="space-y-3 sm:space-y-4 max-w-full min-w-0">
          {/* Stack on narrow screens; two columns from sm */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 min-w-0">
            {/* Record Date */}
            <div className="min-w-0">
            <div className="flex items-center justify-between mb-1">
              <label className="block font-medium text-sm text-gray-700">
                Record Date<span className="text-red-500">*</span>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  name="freezeDate"
                  checked={Boolean(values.freezeDate)}
                  onChange={(e) => handleFreezeCheckboxChange('freezeDate', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  tabIndex={-1}
                />
                <span className="text-sm text-gray-700">Freeze</span>
              </label>
            </div>
            <Field name="recordDate">
              {({ field }: any) => (
                <input
                  {...field}
                  ref={recordDateInputRef}
                  type="date"
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (recordDateInputRef.current) {
                        if (typeof (recordDateInputRef.current as any).showPicker === 'function') {
                          (recordDateInputRef.current as any).showPicker();
                        } else {
                          recordDateInputRef.current.click();
                        }
                        setTimeout(() => {
                          recordDateInputRef.current?.addEventListener('change', () => {
                            moveToNextFieldByName('recordDate', 'dena');
                          }, { once: true });
                        }, 100);
                      }
                    }
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);
                    if (e.target.value) {
                      setTimeout(() => {
                        moveToNextFieldByName('recordDate', 'dena');
                      }, 100);
                    }
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="recordDate"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* Lena (Receiver) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block font-medium text-sm text-gray-700">
                Lena (Receiver)<span className="text-red-500">*</span>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  name="freezeReceiver"
                  checked={Boolean(values.freezeReceiver)}
                  onChange={(e) => handleFreezeCheckboxChange('freezeReceiver', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  tabIndex={-1}
                />
                <span className="text-sm text-gray-700">Freeze</span>
              </label>
            </div>
            <input
              ref={lenaInputRef}
              type="text"
              name="lena"
              placeholder="Party Code (lena)"
              maxLength={50}
              onChange={handleLenaCodeChange}
              onBlur={handleLenaCodeBlur}
              onKeyDown={handleLenaCodeEnter}
              className={`w-full p-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                lenaValidation.error ? 'border-red-600' : lenaValidation.isValid ? 'border-green-600' : 'border-gray-300'
              }`}
            />
            {lenaValidation.error && (
              <div className="text-red-500 text-xs mt-1">{lenaValidation.error}</div>
            )}
            {lenaValidation.isValid && lenaValidation.currentBalance !== undefined && (
              <div className="text-xs mt-1 space-y-0.5">
                <div className="text-gray-600">Current Balance: <span className="font-medium">{(lenaValidation.currentBalance / 100).toFixed(2)}</span></div>
                {values.amount && !isNaN(parseFloat(values.amount)) && (
                  <div className="text-gray-600">Balance After: <span className="font-medium">{((lenaValidation.currentBalance + parseFloat(values.amount)) / 100).toFixed(2)}</span></div>
                )}
              </div>
            )}
            <ErrorMessage
              name="lena"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* Dena (Sender) */}
          <div className="min-w-0">
            <div className="flex items-center justify-between mb-1">
              <label className="block font-medium text-sm text-gray-700">
                Dena (Sender)<span className="text-red-500">*</span>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  name="freezeSender"
                  checked={Boolean(values.freezeSender)}
                  onChange={(e) => handleFreezeCheckboxChange('freezeSender', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  tabIndex={-1}
                />
                <span className="text-sm text-gray-700">Freeze</span>
              </label>
            </div>
            <input
              ref={denaInputRef}
              type="text"
              name="dena"
              placeholder="Party Code (dena)"
              maxLength={50}
              onChange={handleDenaCodeChange}
              onBlur={handleDenaCodeBlur}
              onKeyDown={handleDenaCodeEnter}
              className={`w-full p-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                denaValidation.error ? 'border-red-600' : denaValidation.isValid ? 'border-green-600' : 'border-gray-300'
              }`}
            />
            {denaValidation.error && (
              <div className="text-red-500 text-xs mt-1">{denaValidation.error}</div>
            )}
            {denaValidation.isValid && denaValidation.currentBalance !== undefined && (
              <div className="text-xs mt-1 space-y-0.5">
                <div className="text-gray-600">Current Balance: <span className="font-medium">{(denaValidation.currentBalance / 100).toFixed(2)}</span></div>
                {values.amount && !isNaN(parseFloat(values.amount)) && (
                  <div className="text-gray-600">Balance After: <span className="font-medium">{((denaValidation.currentBalance - parseFloat(values.amount)) / 100).toFixed(2)}</span></div>
                )}
              </div>
            )}
            <ErrorMessage
              name="dena"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* Amount */}
          <div className="min-w-0">
            <label className="block font-medium text-sm text-gray-700 mb-1">
              Amount<span className="text-red-500">*</span>
            </label>
            <Field name="amount">
              {({ field }: any) => (
                <input
                  {...field}
                  ref={amountInputRef}
                  type="text"
                  placeholder="Amount"
                  maxLength={11}
                  value={field.value || ''}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      moveToNextField(amountInputRef, cityInputRef);
                    }
                    // Allow: backspace, delete, tab, escape, enter, arrow keys, home, end, minus sign, and numbers
                    if (!/[0-9-]/.test(e.key) && 
                        !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key) &&
                        !(e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    let value = e.target.value;
                    // Allow digits and minus sign (only at the start)
                    value = value.replace(/[^0-9-]/g, '');
                    // Ensure minus sign is only at the start
                    if (value.includes('-')) {
                      const minusIndex = value.indexOf('-');
                      if (minusIndex !== 0) {
                        value = value.replace(/-/g, '');
                      } else {
                        value = '-' + value.substring(1).replace(/-/g, '');
                      }
                    }
                    // Limit to 11 characters (10 digits + optional minus sign)
                    const limitedValue = value.slice(0, 11);
                    // Update Formik field value
                    field.onChange({
                      target: {
                        name: field.name,
                        value: limitedValue
                      }
                    });
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="amount"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* City */}
          <div className="min-w-0">
            <label className="block font-medium text-sm text-gray-700 mb-1">
              City
            </label>
            <Field name="city">
              {({ field }: any) => (
                <input
                  {...field}
                  ref={cityInputRef}
                  type="text"
                  placeholder="City"
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      moveToNextField(cityInputRef, tokenInputRef);
                    }
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="city"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* Token */}
          <div className="min-w-0">
            <label className="block font-medium text-sm text-gray-700 mb-1">
              Token
            </label>
            <Field name="token">
              {({ field }: any) => (
                <input
                  {...field}
                  ref={tokenInputRef}
                  type="text"
                  placeholder="Token"
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      moveToNextField(tokenInputRef, remarkInputRef);
                    }
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="token"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

            {/* Remark */}
            <div className="col-span-2">
              <label className="block font-medium text-sm text-gray-700 mb-1">
                Remark
              </label>
              <Field name="remark">
                {({ field }: any) => (
                  <input
                    {...field}
                    ref={remarkInputRef}
                    type="text"
                    placeholder="Remark"
                    className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (isSubmittingRef.current) return;
                        if (editId) {
                          saveButtonRef.current?.focus();
                        } else {
                          saveAndContinueButtonRef.current?.click();
                        }
                      }
                    }}
                  />
                )}
              </Field>
              <ErrorMessage
                name="remark"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
            </div>
          </div>

          {/* Buttons Row */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-0 sm:space-x-2 pt-3 sm:pt-4 border-t border-gray-200 mt-3 sm:mt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 text-sm rounded-md w-full sm:w-auto"
            >
              Cancel
            </Button>
            <button
              type="button"
              ref={saveAndContinueButtonRef}
              disabled={isSubmitting || !!editId}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key === 'Enter' && !isSubmitting && !editId) {
                  e.preventDefault();
                  if (isSubmittingRef.current) return;
                  e.currentTarget.click();
                }
              }}
              onClick={async () => {
                if (isSubmittingRef.current) return;
                isSubmittingRef.current = true;
                setSubmitting(true);
                try {
                  // Validate party fields first
                  if (!denaValidation.isValid || denaValidation.partyId === null) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                      detail: { message: 'Please enter a valid DENA party code', type: 'error' }
                    }));
                    setSubmitting(false);
                    denaInputRef.current?.focus();
                    return;
                  }
                  
                  if (!lenaValidation.isValid || lenaValidation.partyId === null) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                      detail: { message: 'Please enter a valid LENA party code', type: 'error' }
                    }));
                    setSubmitting(false);
                    lenaInputRef.current?.focus();
                    return;
                  }
                  
                  // Validate that parties are different
                  if (denaValidation.partyId === lenaValidation.partyId) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                      detail: { message: 'LENA and DENA cannot be the same party', type: 'error' }
                    }));
                    setSubmitting(false);
                    return;
                  }
                  
                  // Validate form fields (recordDate, amount, etc.)
                  const formErrors = await validateForm();
                  if (formErrors && Object.keys(formErrors).length > 0) {
                    setSubmitting(false);
                    return; // Don't proceed if there are validation errors
                  }

                  // Capture freeze / party state before async work
                  const preserveLena = Boolean(values.freezeReceiver);
                  const preserveDena = Boolean(values.freezeSender);
                  const preserveRecordDate = Boolean(values.freezeDate);
                  const preserveFreezeDate = Boolean(values.freezeDate);
                  const preserveFreezeReceiver = Boolean(values.freezeReceiver);
                  const preserveFreezeSender = Boolean(values.freezeSender);
                  const savedRecordDate = values.recordDate;
                  const savedLenaCode = lenaCode;
                  const savedValidatedLenaCode = validatedLenaCode;
                  const savedLenaValidation = { ...lenaValidation };
                  const savedLenaInputValue = lenaInputRef.current?.value || '';
                  const savedDenaCode = denaCode;
                  const savedValidatedDenaCode = validatedDenaCode;
                  const savedDenaValidation = { ...denaValidation };
                  const savedDenaInputValue = denaInputRef.current?.value || '';

                  // Call handleSubmit with formikHelpers to manage submitting state
                  const success = await handleSubmit(values, { setSubmitting } as any);
                  if (success) {
                    // Re-fetch balances; apply them AFTER freeze restore so frozen parties stay intact
                    const refreshedBalances = await fetchPartiesAndUpdateBalance();
                    
                    // Save & Continue: Clear form while preserving frozen fields
                    setSubmitting(false);
                    
                    // Determine record date: keep current if frozen, otherwise set to today
                    const newRecordDate = preserveRecordDate ? savedRecordDate : getTodayDate();
                    
                    // Prepare new form values - preserve frozen fields and freeze checkbox states
                    const newFormValues: HavalaFormValues = {
                      recordDate: newRecordDate,
                      lena: preserveLena ? savedLenaCode : "",
                      dena: preserveDena ? savedDenaCode : "",
                      amount: "",
                      city: "",
                      remark: "",
                      token: "",
                      freezeDate: preserveFreezeDate,
                      freezeReceiver: preserveFreezeReceiver,
                      freezeSender: preserveFreezeSender,
                    };
                    
                    // Reset form values FIRST
                    resetForm({
                      values: newFormValues
                    });
                    
                    // Then update state and input fields AFTER resetForm, then focus Lend (Receiver)
                    setTimeout(() => {
                      
                      // Explicitly set freeze checkbox values using setFieldValue to ensure they're synced
                      setFieldValue('freezeDate', preserveFreezeDate, false);
                      setFieldValue('freezeReceiver', preserveFreezeReceiver, false);
                      setFieldValue('freezeSender', preserveFreezeSender, false);
                      
                      // Handle Record Date field - ensure it's set correctly
                      if (preserveRecordDate) {
                        setFieldValue('recordDate', savedRecordDate, false);
                        if (recordDateInputRef.current) {
                          recordDateInputRef.current.value = savedRecordDate;
                        }
                      }
                      
                      // Handle Lena field
                      if (preserveLena) {
                        // Preserve Lena: restore state and input value, then apply refreshed balance
                        setLenaCode(savedLenaCode);
                        setValidatedLenaCode(savedValidatedLenaCode);
                        setLenaValidation({
                          ...savedLenaValidation,
                          ...(refreshedBalances.lenaBalance !== undefined
                            ? { currentBalance: refreshedBalances.lenaBalance }
                            : {}),
                        });
                        if (lenaInputRef.current) {
                          if (savedLenaInputValue) {
                            lenaInputRef.current.value = savedLenaInputValue;
                          } else if (savedLenaValidation.partyName && savedLenaCode) {
                            const restoredValue = `${savedLenaCode} - ${savedLenaValidation.partyName}`;
                            lenaInputRef.current.value = restoredValue;
                          }
                        }
                      } else {
                        // Clear Lena: reset state and input value
                        setLenaCode('');
                        setValidatedLenaCode('');
                        setLenaValidation({ isValid: false, error: '', partyName: '', partyId: null });
                        if (lenaInputRef.current) {
                          lenaInputRef.current.value = '';
                        }
                      }
                      
                      // Handle Dena field
                      if (preserveDena) {
                        // Preserve Dena: restore state and input value, then apply refreshed balance
                        setDenaCode(savedDenaCode);
                        setValidatedDenaCode(savedValidatedDenaCode);
                        setDenaValidation({
                          ...savedDenaValidation,
                          ...(refreshedBalances.denaBalance !== undefined
                            ? { currentBalance: refreshedBalances.denaBalance }
                            : {}),
                        });
                        if (denaInputRef.current) {
                          if (savedDenaInputValue) {
                            denaInputRef.current.value = savedDenaInputValue;
                          } else if (savedDenaValidation.partyName && savedDenaCode) {
                            const restoredValue = `${savedDenaCode} - ${savedDenaValidation.partyName}`;
                            denaInputRef.current.value = restoredValue;
                          }
                        }
                      } else {
                        // Clear Dena: reset state and input value
                        setDenaCode('');
                        setValidatedDenaCode('');
                        setDenaValidation({ isValid: false, error: '', partyName: '', partyId: null });
                        if (denaInputRef.current) {
                          denaInputRef.current.value = '';
                        }
                      }
                      
                      // Focus Lend (Receiver) after restore so it runs after React re-render; when Lena is frozen, focus next field
                      const focusAfterRestore = () => {
                        let focusTarget: React.RefObject<HTMLInputElement | null> | null = null;
                        if (preserveLena) {
                          if (preserveDena) {
                            focusTarget = amountInputRef;
                          } else {
                            focusTarget = denaInputRef;
                          }
                        } else {
                          focusTarget = lenaInputRef;
                        }
                        focusTarget?.current?.focus();
                      };
                      setTimeout(focusAfterRestore, 50);
                    }, 200);
                  } else {
                    setSubmitting(false);
                  }
                } catch (error) {
                  setSubmitting(false);
                } finally {
                  isSubmittingRef.current = false;
                }
              }}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isSubmitting ? "Saving..." : "Save & Continue"}
            </button>
            <button
              type="button"
              ref={saveButtonRef}
              disabled={isSubmitting}
              onClick={async () => {
                if (isSubmittingRef.current) return;
                isSubmittingRef.current = true;
                setSubmitting(true);
                try {
                  // Validate party fields first
                  if (!denaValidation.isValid || denaValidation.partyId === null) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                      detail: { message: 'Please enter a valid DENA party code', type: 'error' }
                    }));
                    setSubmitting(false);
                    denaInputRef.current?.focus();
                    return;
                  }
                  
                  if (!lenaValidation.isValid || lenaValidation.partyId === null) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                      detail: { message: 'Please enter a valid LENA party code', type: 'error' }
                    }));
                    setSubmitting(false);
                    lenaInputRef.current?.focus();
                    return;
                  }
                  
                  // Validate that parties are different
                  if (denaValidation.partyId === lenaValidation.partyId) {
                    window.dispatchEvent(new CustomEvent('show-toast', {
                      detail: { message: 'LENA and DENA cannot be the same party', type: 'error' }
                    }));
                    setSubmitting(false);
                    return;
                  }
                  
                  // Validate form fields (recordDate, amount, etc.)
                  const formErrors = await validateForm();
                  if (formErrors && Object.keys(formErrors).length > 0) {
                    setSubmitting(false);
                    return; // Don't proceed if there are validation errors
                  }
                  
                  // Call handleSubmit with formikHelpers to manage submitting state
                  const success = await handleSubmit(values, { setSubmitting } as any);
                  if (success) {
                    // Save: Close drawer
                    setSubmitting(false);
                    onClose();
                  } else {
                    setSubmitting(false);
                  }
                } catch (error) {
                  setSubmitting(false);
                } finally {
                  isSubmittingRef.current = false;
                }
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </Form>
        );
      }}
    </Formik>
  );
};

export default CreateHavalaSideDrawerDialog;

